'use client';

import clsx from 'clsx';
import { useCallback, useState, useTransition } from 'react';

import { getTickerKey } from '@/modules/ticker/selectedTickerCookie';
import { Ticker } from '@/modules/ticker/Ticker';
import { TickerSummary } from '@/modules/ticker/TickerSummary';
import { TLTicker } from '@/modules/ticker/TLTicker';
import {
  TickerActivityRecord,
  TickerDataInterface,
  TLTickerDataInterface,
} from '@/modules/ticker/types';
import { getTeamLeaguesTicker2 } from '@/utils/actions/homepage/getTeamLeaguesTicker2';
import { getTournamentsTicker2 } from '@/utils/actions/homepage/getTournamentsTicker2';
import { setSelectedTicker } from '@/utils/actions/homepage/setSelectedTicker';

interface TickerClientWrapperProps {
  activeTickers: TickerActivityRecord[];
  initialSelectedIndex: number;
  initialTournamentData: TickerDataInterface | null;
  initialTeamLeagueData: TLTickerDataInterface | null;
}

export function TickerClientWrapper({
  activeTickers,
  initialSelectedIndex,
  initialTournamentData,
  initialTeamLeagueData,
}: TickerClientWrapperProps) {
  const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex);
  const [tournamentData, setTournamentData] = useState<TickerDataInterface | null>(
    initialTournamentData
  );
  const [teamLeagueData, setTeamLeagueData] = useState<TLTickerDataInterface | null>(
    initialTeamLeagueData
  );
  const [isPending, startTransition] = useTransition();

  const selectedTicker = activeTickers[selectedIndex];

  const handleTickerSelect = useCallback(
    (index: number) => {
      const ticker = activeTickers[index];
      if (!ticker) return;

      setSelectedIndex(index);

      // Fire-and-forget — persist selection in a per-device cookie so the
      // same item is preselected on the user's next visit. Failure here
      // must not block the UI.
      void setSelectedTicker(getTickerKey(ticker));

      startTransition(async () => {
        if (ticker.type === 'tournament') {
          const result = await getTournamentsTicker2({ partner: ticker.partnersFlag });
          setTeamLeagueData(null);
          setTournamentData(result.data && result.data.matches.length > 0 ? result.data : null);
        } else if (ticker.type === 'team_league') {
          if (!ticker.teamLeagueUuid || !ticker.organizationUuid || !ticker.activeSeasonUuid)
            return;
          const result = await getTeamLeaguesTicker2({
            teamLeagueUuid: ticker.teamLeagueUuid,
            organizationUuid: ticker.organizationUuid,
            activeSeasonUuid: ticker.activeSeasonUuid,
            utcOffset: ticker.utcOffset,
          });
          setTournamentData(null);
          setTeamLeagueData(result.data && result.data.matchups.length > 0 ? result.data : null);
        }
      });
    },
    [activeTickers]
  );

  const renderTicker = () => {
    if (!selectedTicker) return null;

    if (selectedTicker.type === 'tournament' && tournamentData) {
      return (
        <Ticker
          key={`tournament-${selectedIndex}`}
          initialMatches={tournamentData}
          partner={selectedTicker.partnersFlag}
        />
      );
    }

    if (
      selectedTicker.type === 'team_league' &&
      teamLeagueData &&
      selectedTicker.teamLeagueUuid &&
      selectedTicker.organizationUuid
    ) {
      return (
        <TLTicker
          key={`tl-${selectedIndex}`}
          initialMatches={teamLeagueData}
          teamLeagueUuid={selectedTicker.teamLeagueUuid}
          organizationUuid={selectedTicker.organizationUuid}
        />
      );
    }

    return null;
  };

  const tickerContent = renderTicker();

  const enableOnMobile = activeTickers.length > 1;

  return (
    <div className='bg-white' id='pb-ticker'>
      <div className='mx-auto flex max-w-[1440px] h-[148px]'>
        <div className={clsx({ 'hidden sm:flex': !enableOnMobile, flex: enableOnMobile })}>
          <TickerSummary
            activeTickers={activeTickers}
            selectedIndex={selectedIndex}
            onTickerSelect={handleTickerSelect}
          />
        </div>
        {isPending ? (
          <div className='flex flex-1 items-center justify-center py-4'>
            <span className='text-sm text-gray-500'>Loading...</span>
          </div>
        ) : tickerContent ? (
          tickerContent
        ) : (
          <div className='flex flex-1 items-center justify-center py-4'>
            <span className='text-sm text-gray-500'>No matches to display</span>
          </div>
        )}
      </div>
    </div>
  );
}
